import { createClient } from '@supabase/supabase-js'

// Supabase configuration
const supabaseUrl = 'https://akuoggticrcsctgkerfl.supabase.co'
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFrdW9nZ3RpY3Jjc2N0Z2tlcmZsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc3MzkxNDEsImV4cCI6MjA3MzMxNTE0MX0.Wg4hSxLkvQD36mMhGZ6vTcXdfYIgVCZAAcsMLdvIljc'

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Authentication service functions
export const authService = {
  // Sign up with email and password
  async signUp(email, password, userData = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
        emailRedirectTo: `${window.location.origin}/login`
      }
    })
    if (error) throw error
    return data
  },

  // Sign in with email and password
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    if (error) throw error

    // Approval gate: block disapproved customers
    try {
      const { data: customer, error: cErr } = await supabase
        .from('customers')
        .select('id, is_approved')
        .eq('email', email)
        .maybeSingle()
      if (cErr || !customer || customer.is_approved === false) {
        await supabase.auth.signOut()
        throw new Error('Your account is not approved yet. Please contact support.')
      }
    } catch (gateErr) {
      // Bubble gate failure as auth error to caller
      throw gateErr
    }

    return data
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  // Get current user
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback)
  },

  // Reset password
  async resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) throw error
    return data
  },

  // Update user profile
  async updateUser(updates) {
    const { data, error } = await supabase.auth.updateUser(updates)
    if (error) throw error
    return data
  },

  // Check approval for current session and sign out if disapproved
  async enforceApprovalForCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.email) return true
    const { data: customer } = await supabase
      .from('customers')
      .select('is_approved')
      .eq('email', user.email)
      .maybeSingle()
    if (!customer || customer.is_approved === false) {
      await supabase.auth.signOut()
      return false
    }
    return true
  }
}

// Database service functions
export const dbService = {
  // Food Items
  async getFoodItems() {
    const { data, error } = await supabase
      .from('food_items')
      .select('*')
      .order('name')
    if (error) throw error
    return data
  },

  async getFoodItemById(id) {
    const { data, error } = await supabase
      .from('food_items')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  // Grocery Items
  async getGroceryItems() {
    const { data, error } = await supabase
      .from('grocery_items')
      .select(`
        *,
        shop:shops(name, shop_type)
      `)
      .order('name')
    if (error) throw error
    return data
  },

  // Pharmacies (separate from shops)
  async getPharmacies(countryName = null, cityName = null) {
    let query = supabase
      .from('pharmacies')
      .select(`
        *,
        orders!pharmacy_id(
          status,
          created_at
        )
      `)
      .eq('is_online', true)
    
    // Filter by country if provided
    if (countryName) {
      query = query.eq('country', countryName)
    }
    
    // Filter by city if provided
    if (cityName) {
      query = query.eq('city', cityName)
    }
    
    query = query.order('name')
    const { data, error } = await query
    if (error) throw error;
    return data.map(pharmacy => {
      const completedOrders = pharmacy.orders?.filter(order => order.status === 'completed') || [];
      const totalOrders = completedOrders.length;
      let calculatedRating = pharmacy.rating || 0;
      if (totalOrders > 0) {
        const recentOrders = completedOrders.filter(order => {
          const orderDate = new Date(order.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return orderDate >= thirtyDaysAgo;
        });
        const volumeScore = Math.min(totalOrders / 10, 1);
        const recentActivityScore = Math.min(recentOrders.length / 5, 1);
        calculatedRating = 3.0 + (volumeScore * 1.5) + (recentActivityScore * 0.5);
        calculatedRating = Math.min(calculatedRating, 5.0);
      }
      const { orders, ...pharmacyWithoutOrders } = pharmacy;
      return { ...pharmacyWithoutOrders, rating: calculatedRating, total_orders: totalOrders };
    });
  },

  async getPharmacyById(id) {
    const { data, error } = await supabase
      .from('pharmacies')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  async getPharmacyItems() {
    const { data, error } = await supabase
      .from('pharmacy_items')
      .select(`
        *,
        pharmacy:pharmacies(name)
      `)
      .order('name');
    if (error) throw error;
    return data;
  },

  async getPharmacyItemById(id) {
    const { data, error } = await supabase
      .from('pharmacy_items')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  async getGroceryItemById(id) {
    const { data, error } = await supabase
      .from('grocery_items')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  // Categories
  async getFoodCategories() {
    const { data, error } = await supabase
      .from('food_categories')
      .select('*')
      .order('name')
    if (error) throw error
    return data
  },

  async getGroceryCategories() {
    const { data, error } = await supabase
      .from('grocery_categories')
      .select('*')
      .order('name')
    if (error) throw error
    return data
  },

  // Restaurants (vendors)
  async getRestaurants(countryName = null, cityName = null) {
    let query = supabase
      .from('vendors')
      .select(`
        *,
        orders!vendor_id(
          status,
          created_at
        )
      `)
      .eq('is_online', true) // Only show online vendors
    
    // Filter by country if provided
    if (countryName) {
      query = query.eq('country', countryName)
    }
    
    // Filter by city if provided
    if (cityName) {
      query = query.eq('city', cityName)
    }
    
    query = query.order('name')
    const { data, error } = await query
    if (error) throw error
    
    // Calculate real ratings based on completed orders
    return data.map(vendor => {
      const completedOrders = vendor.orders?.filter(order => order.status === 'completed') || [];
      const totalOrders = completedOrders.length;
      
      let calculatedRating = vendor.rating || 0;
      
      if (totalOrders > 0) {
        // Simple rating calculation based on order completion rate and recency
        const recentOrders = completedOrders.filter(order => {
          const orderDate = new Date(order.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return orderDate >= thirtyDaysAgo;
        });
        
        // Base rating on order volume and recent activity
        const volumeScore = Math.min(totalOrders / 10, 1); // Max 1 point for 10+ orders
        const recentActivityScore = Math.min(recentOrders.length / 5, 1); // Max 1 point for 5+ recent orders
        calculatedRating = 3.0 + (volumeScore * 1.5) + (recentActivityScore * 0.5); // 3.0 base + up to 2.0 bonus
        calculatedRating = Math.min(calculatedRating, 5.0); // Cap at 5.0
      }
      
      // Remove orders from response to keep it clean
      const { orders, ...vendorWithoutOrders } = vendor;
      
      return {
        ...vendorWithoutOrders,
        rating: calculatedRating,
        total_orders: totalOrders
      };
    });
  },

  async getRestaurantById(id) {
    const { data, error } = await supabase
      .from('vendors')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  // Shops (grocery stores)
  async getShops(countryName = null, cityName = null) {
    let query = supabase
      .from('shops')
      .select(`
        *,
        orders!shop_id(
          status,
          created_at
        )
      `)
      .eq('is_online', true)
    
    // Filter by country if provided
    if (countryName) {
      query = query.eq('country', countryName)
    }
    
    // Filter by city if provided
    if (cityName) {
      query = query.eq('city', cityName)
    }
    
    query = query.order('name')
    const { data, error } = await query
    if (error) throw error
    
    // Calculate real ratings based on completed orders
    return data.map(shop => {
      const completedOrders = shop.orders?.filter(order => order.status === 'completed') || [];
      const totalOrders = completedOrders.length;
      
      let calculatedRating = shop.rating || 0;
      
      if (totalOrders > 0) {
        // Simple rating calculation based on order completion rate and recency
        const recentOrders = completedOrders.filter(order => {
          const orderDate = new Date(order.created_at);
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          return orderDate >= thirtyDaysAgo;
        });
        
        // Base rating on order volume and recent activity
        const volumeScore = Math.min(totalOrders / 10, 1); // Max 1 point for 10+ orders
        const recentActivityScore = Math.min(recentOrders.length / 5, 1); // Max 1 point for 5+ recent orders
        calculatedRating = 3.0 + (volumeScore * 1.5) + (recentActivityScore * 0.5); // 3.0 base + up to 2.0 bonus
        calculatedRating = Math.min(calculatedRating, 5.0); // Cap at 5.0
      }
      
      // Remove orders from response to keep it clean
      const { orders, ...shopWithoutOrders } = shop;
      
      return {
        ...shopWithoutOrders,
        rating: calculatedRating,
        total_orders: totalOrders
      };
    });
  },

  async getShopById(id) {
    const { data, error } = await supabase
      .from('shops')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  // Users
  async getCustomers() {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('name')
    if (error) throw error
    return data
  },

  async getCustomerById(id) {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  async createCustomer(customerData) {
    console.log('Creating customer with data:', customerData);
    const { data, error } = await supabase
      .from('customers')
      .insert([customerData])
      .select()
      .single()
    if (error) {
      console.error('Customer creation error:', error);
      throw error;
    }
    console.log('Customer created successfully:', data);
    return data
  },

  async getCustomerByEmail(email) {
    // Be robust against accidental duplicate email rows: take latest one
    const query = supabase
      .from('customers')
      .select('*')
      .eq('email', email)
      .order('created_at', { ascending: false })
      .limit(1);
    // maybeSingle avoids throwing on 0 rows; limit(1) avoids 406 on >1
    const { data, error } = await query.maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return data || null;
  },

  async getCustomerByAuthUid(authUid) {
    if (!authUid) return null;
    const query = supabase
      .from('customers')
      .select('*')
      .eq('auth_uid', authUid)
      .order('created_at', { ascending: false })
      .limit(1);
    const { data, error } = await query.maybeSingle();
    if (error && error.code !== 'PGRST116') throw error;
    return data || null;
  },

  async createOrUpdateCustomerByEmail(email, customerData) {
    // First try to get existing customer
    const existingCustomer = await this.getCustomerByEmail(email);
    
    if (existingCustomer) {
      // Update existing customer
      return await this.updateCustomer(existingCustomer.id, customerData);
    } else {
      // Create new customer
      return await this.createCustomer({ ...customerData, email });
    }
  },

  async updateCustomer(id, customerData) {
    const { data, error } = await supabase
      .from('customers')
      .update(customerData)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateCustomerByEmail(email, customerData) {
    const { data, error } = await supabase
      .from('customers')
      .update(customerData)
      .eq('email', email)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Orders
  async getOrders() {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        customer:customers(name, email, phone),
        vendor:vendors(name),
        shop:shops(name),
        order_items:order_items(*)
      `)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data
  },

  async getOrderById(id) {
    // Use minimal selection to avoid RLS blocks from related tables
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .eq('id', id)
      .single();
    if (error) throw error;
    return data;
  },

  async getOrdersByCustomer(customerId) {
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data
  },

  async createOrder(orderData) {
    const { data, error } = await supabase
      .from('orders')
      .insert([orderData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateOrderStatus(id, status) {
    const { data, error } = await supabase
      .from('orders')
      .update({ status })
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Order Items
  async createOrderItems(orderItems) {
    const { data, error } = await supabase
      .from('order_items')
      .insert(orderItems)
      .select()
    if (error) throw error
    return data
  },

  // Promotions
  async getPromotions() {
    const { data, error } = await supabase
      .from('promotions')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) throw error
    return data
  },

  // Offers
  async getOffers(section = null, countryCode = null) {
    try {
      console.log('🔍 Starting getOffers query...');
      console.log('📋 Requested filters - section:', section, 'countryCode:', countryCode);
      
      // First, try to fetch ALL offers (not filtered by is_active) to see if RLS is blocking
      let testQuery = supabase
        .from('offers')
        .select('*')
        .limit(10);
      
      const { data: testData, error: testError } = await testQuery;
      
      if (testError) {
        console.error('❌ Error fetching offers (test query):', testError);
        console.error('Error code:', testError.code);
        console.error('Error message:', testError.message);
        console.error('Error details:', testError.details);
        console.error('Error hint:', testError.hint);
        
        // If RLS is blocking, return empty array
        if (testError.code === '42501' || testError.message?.includes('row-level security')) {
          console.error('🚫 RLS policy is blocking access to offers table!');
          console.error('💡 Solution: Run FIX_OFFERS_RLS_SIMPLE.sql in Supabase SQL Editor');
          return [];
        }
        
        throw testError;
      }
      
      console.log('✅ Test query successful. Found', testData?.length || 0, 'total offers (before filtering)');
      if (testData && testData.length > 0) {
        console.log('📋 All offers in database:', testData.map(o => ({
          id: o.id,
          title: o.title,
          section: o.section,
          is_active: o.is_active,
          start_date: o.start_date,
          end_date: o.end_date
        })));
      }
      
      // Now fetch only active offers
      let query = supabase
        .from('offers')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('❌ Error fetching active offers:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        throw error;
      }
      
      console.log('✅ Fetched all active offers from database:', data?.length || 0, 'offers');
      if (data && data.length > 0) {
        console.log('📋 Active offers details:', data.map(o => ({
          id: o.id,
          title: o.title,
          section: o.section,
          is_active: o.is_active,
          start_date: o.start_date,
          end_date: o.end_date,
          country_code: o.country_code,
          priority: o.priority
        })));
      } else {
        console.warn('⚠️ No active offers found! Check if offers have is_active=true in database.');
      }
      
      // Filter in JavaScript for more reliable date and location matching
      const now = new Date();
      let filtered = (data || []).filter(offer => {
        // Check date range
        const startDate = offer.start_date ? new Date(offer.start_date) : null;
        const endDate = offer.end_date ? new Date(offer.end_date) : null;
        
        // Offer must have started (or no start date)
        if (startDate && now < startDate) {
          console.log(`⏰ Offer ${offer.id} (${offer.title}) hasn't started yet. Start date: ${startDate}`);
          return false;
        }
        
        // Offer must not have ended (or no end date)
        if (endDate && now > endDate) {
          console.log(`⏰ Offer ${offer.id} (${offer.title}) has ended. End date: ${endDate}`);
          return false;
        }
        
        // Filter by country if provided (but be lenient - show global offers too)
        if (countryCode) {
          // Show global offers (no country_code) or matching country
          if (offer.country_code && offer.country_code.toUpperCase() !== countryCode.toUpperCase()) {
            console.log(`🌍 Offer ${offer.id} (${offer.title}) country mismatch. Offer: ${offer.country_code}, User: ${countryCode}`);
            // Don't filter out - be lenient with country matching
            // return false;
          }
        }
        
        // Filter by section if provided (but be lenient - show 'all' section offers)
        if (section && section !== 'all') {
          // Show 'all' section offers or matching section
          if (offer.section !== 'all' && offer.section !== section) {
            console.log(`📂 Offer ${offer.id} (${offer.title}) section mismatch. Offer: ${offer.section}, Current: ${section}`);
            // Don't filter out - be lenient with section matching to show all offers
            // return false;
          }
        }
        
        return true;
      });
      
      // If no offers match the strict filters, show all active offers regardless of section/country
      if (filtered.length === 0 && data && data.length > 0) {
        console.warn('⚠️ No offers matched strict filters. Showing all active offers regardless of section/country.');
        filtered = data.filter(offer => {
          const startDate = offer.start_date ? new Date(offer.start_date) : null;
          const endDate = offer.end_date ? new Date(offer.end_date) : null;
          if (startDate && now < startDate) return false;
          if (endDate && now > endDate) return false;
          return true;
        });
      }
      
      console.log('✅ Filtered offers (final):', filtered.length, filtered.map(o => ({
        id: o.id,
        title: o.title,
        section: o.section
      })));
      return filtered;
    } catch (error) {
      console.error('❌ Error in getOffers:', error);
      // Return empty array on error instead of throwing
      return [];
    }
  },

  // Vendors
  async getVendors() {
    const { data, error } = await supabase
      .from('vendors')
      .select('*')
      .order('name')
    if (error) throw error
    return data
  },

  async getVendorById(id) {
    const { data, error } = await supabase
      .from('vendors')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  async createVendor(vendorData) {
    const { data, error } = await supabase
      .from('vendors')
      .insert([vendorData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateVendor(id, vendorData) {
    const { data, error } = await supabase
      .from('vendors')
      .update(vendorData)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Vendor Restaurant Management
  async createRestaurant(restaurantData) {
    const { data, error } = await supabase
      .from('vendors')
      .insert([restaurantData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateRestaurant(id, restaurantData) {
    const { data, error } = await supabase
      .from('vendors')
      .update(restaurantData)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Vendor Shop Management
  async createShop(shopData) {
    const { data, error } = await supabase
      .from('shops')
      .insert([shopData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateShop(id, shopData) {
    const { data, error } = await supabase
      .from('shops')
      .update(shopData)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Vendor Food Item Management
  async createFoodItem(foodItemData) {
    const { data, error } = await supabase
      .from('food_items')
      .insert([foodItemData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateFoodItem(id, foodItemData) {
    const { data, error } = await supabase
      .from('food_items')
      .update(foodItemData)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Vendor Grocery Item Management
  async createGroceryItem(groceryItemData) {
    const { data, error } = await supabase
      .from('grocery_items')
      .insert([groceryItemData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async updateGroceryItem(id, groceryItemData) {
    const { data, error } = await supabase
      .from('grocery_items')
      .update(groceryItemData)
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Real-time subscriptions
  subscribeToOrders(callback) {
    return supabase
      .channel('orders')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'orders' }, 
        callback
      )
      .subscribe()
  },

  subscribeToOrder(id, onChange) {
    return supabase
      .channel(`orders-${id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `id=eq.${id}` }, onChange)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items', filter: `order_id=eq.${id}` }, onChange)
      .subscribe()
  },

  subscribeToOrderItems(callback) {
    return supabase
      .channel('order_items')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'order_items' }, 
        callback
      )
      .subscribe()
  }
  ,
  // Realtime for shops and grocery items
  subscribeToShops(callback) {
    return supabase
      .channel('shops')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'shops' }, callback)
      .subscribe();
  },
  subscribeToGroceryItems(callback) {
    return supabase
      .channel('grocery_items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'grocery_items' }, callback)
      .subscribe();
  },

  subscribeToClothingStores(callback) {
    return supabase
      .channel('clothing_stores')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clothing_stores' }, callback)
      .subscribe();
  },

  subscribeToClothingItems(callback) {
    return supabase
      .channel('clothing_items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'clothing_items' }, callback)
      .subscribe();
  },

  subscribeToAccessoriesStores(callback) {
    return supabase
      .channel('accessories_stores')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'accessories_stores' }, callback)
      .subscribe();
  },

  subscribeToAccessoriesItems(callback) {
    return supabase
      .channel('accessories_items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'accessories_items' }, callback)
      .subscribe();
  },

  subscribeToElectronicsStores(callback) {
    return supabase
      .channel('electronics_stores')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'electronics_stores' }, callback)
      .subscribe();
  },

  subscribeToElectronicsItems(callback) {
    return supabase
      .channel('electronics_items')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'electronics_items' }, callback)
      .subscribe();
  },

  // Support Tickets
  async createSupportTicket(ticketData) {
    const { data, error } = await supabase
      .from('support_tickets')
      .insert([ticketData])
      .select()
      .single()
    if (error) throw error
    return data
  },

  async getSupportTicketsByCustomer(customerId) {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data
  },

  async updateSupportTicketStatus(id, status) {
    const { data, error } = await supabase
      .from('support_tickets')
      .update({ status })
      .eq('id', id)
      .select()
      .single()
    if (error) throw error
    return data
  },

  // Customer Preferences
  async updateCustomerPreferences(customerId, preferences) {
    const { data, error } = await supabase
      .from('customers')
      .update({ preferences })
      .eq('id', customerId)
      .select()
      .single()
    if (error) throw error
    return data
  },

  async getCustomerPreferences(customerId) {
    const { data, error } = await supabase
      .from('customers')
      .select('preferences')
      .eq('id', customerId)
      .single()
    if (error) throw error
    return data?.preferences || {
      notifications: true,
      emailUpdates: true,
      smsUpdates: false,
      promotions: true
    }
  },

  // Clothing Stores
  async getClothingStores(countryName = null, cityName = null) {
    let query = supabase
      .from('clothing_stores')
      .select('*')
      .eq('is_online', true)
    
    // Filter by country if provided
    if (countryName) {
      query = query.eq('country', countryName)
    }
    
    // Filter by city if provided
    if (cityName) {
      query = query.eq('city', cityName)
    }
    
    query = query.order('name')
    const { data, error } = await query
    if (error) {
      console.error('Error fetching clothing stores:', error);
      throw error;
    }
    console.log('Fetched clothing stores:', data?.length || 0, data);
    return data || []
  },

  async getClothingStoreById(id) {
    const { data, error } = await supabase
      .from('clothing_stores')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  async getClothingItems() {
    const { data, error } = await supabase
      .from('clothing_items')
      .select('*')
      .eq('is_available', true)
      .order('name')
    if (error) {
      console.error('Error fetching clothing items:', error);
      throw error;
    }
    console.log('Fetched clothing items:', data?.length || 0, data);
    return data || []
  },

  async getClothingItemById(id) {
    const { data, error } = await supabase
      .from('clothing_items')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  // Accessories Stores
  async getAccessoriesStores(countryName = null, cityName = null) {
    let query = supabase
      .from('accessories_stores')
      .select('*')
      .eq('is_online', true)
    
    // Filter by country if provided
    if (countryName) {
      query = query.eq('country', countryName)
    }
    
    // Filter by city if provided
    if (cityName) {
      query = query.eq('city', cityName)
    }
    
    query = query.order('name')
    const { data, error } = await query
    if (error) {
      console.error('Error fetching accessories stores:', error);
      throw error;
    }
    console.log('Fetched accessories stores:', data?.length || 0, data);
    return data || []
  },

  async getAccessoriesStoreById(id) {
    const { data, error } = await supabase
      .from('accessories_stores')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  async getAccessoriesItems() {
    const { data, error } = await supabase
      .from('accessories_items')
      .select('*')
      .eq('is_available', true)
      .order('name')
    if (error) {
      console.error('Error fetching accessories items:', error);
      throw error;
    }
    console.log('Fetched accessories items:', data?.length || 0, data);
    return data || []
  },

  async getAccessoriesItemById(id) {
    const { data, error } = await supabase
      .from('accessories_items')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  // Electronics Stores
  async getElectronicsStores(countryName = null, cityName = null) {
    let query = supabase
      .from('electronics_stores')
      .select('*')
      .eq('is_online', true)
    
    // Filter by country if provided
    if (countryName) {
      query = query.eq('country', countryName)
    }
    
    // Filter by city if provided
    if (cityName) {
      query = query.eq('city', cityName)
    }
    
    query = query.order('name')
    const { data, error } = await query
    if (error) {
      console.error('Error fetching electronics stores:', error);
      throw error;
    }
    console.log('Fetched electronics stores:', data?.length || 0, data);
    return data || []
  },

  async getElectronicsStoreById(id) {
    const { data, error } = await supabase
      .from('electronics_stores')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  },

  async getElectronicsItems() {
    const { data, error } = await supabase
      .from('electronics_items')
      .select('*')
      .eq('is_available', true)
      .order('name')
    if (error) {
      console.error('Error fetching electronics items:', error);
      throw error;
    }
    console.log('Fetched electronics items:', data?.length || 0, data);
    return data || []
  },

  async getElectronicsItemById(id) {
    const { data, error } = await supabase
      .from('electronics_items')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data
  }
}
